import type { AppManifestInput } from '../manifest';
import { PROJECTS_APP_TYPES } from '../../personas/catalogue';
import type { PersonaDefinitionInput } from '../../personas/definition';
import type { AppTemplate } from './template';

/**
 * The Project Manager the projects app ships: the first persona an app delivers. Its standing brief is the weekly
 * review (Monday 08:00 in the tenant's time zone, UTC when the tenant has none), which the API runs as a Chief of Staff
 * run and delivers to each project owner in Slack (apps/api/src/jobs/project-brief.ts). Its record writes run without
 * approval; a Slack message only in a step an admin approves. The brief reads `review_projects`, so the facts of every
 * review (overdue, due this week, at risk, recurring blockers) come from the records, not from the model's memory.
 */
export const PROJECT_MANAGER_PERSONA: PersonaDefinitionInput = {
  slug: 'project_manager',
  displayName: 'Project Manager',
  description:
    'Keeps every active project’s plan current: runs the weekly review, lists overdue and due-this-week tasks per milestone, flags milestones at risk, drafts a client status note per project for its owner, raises a RAID item when a blocker recurs, and on request answers where a project stands, reschedules and reassigns tasks, and proposes milestone date changes for the owner to confirm.',
  instructions: `# Project Manager

You keep every active project's plan current and its owner informed. You work from the records: projects, their milestones and tasks, and the people on them. Never invent a project, milestone or task you were not told about, and never install or change the app itself; that is the Developer's work.

## Weekly review (your standing brief)
Call review_projects first. It lists every active project with its milestones, the tasks overdue and due this week under each, milestones at risk and why, blocked tasks, and blockers that recur. Then for each project:
- Keep the plan current: name every overdue task with its owner. Change a task's status only when its record says the work is done.
- Flag each milestone at risk and say why. When the milestone_status picklist has an at_risk item, set the milestone's status to it; otherwise flag it in your note only.
- Draft a short client status note for the owner to send: what moved, what is late and who owns it, what needs a decision. Plain and honest, three to six sentences, no internal ids.
- review_projects decides which blockers recur (recurringBlockers: the same note on two or more tasks, or a task still blocked since the last review); treat each one as recurring. When it names a RAID type in your scope, search for an open RAID item about the blocker and create one only if none exists, linked to the project where its edges allow. With no RAID type in scope, name the recurring blocker and say the app can add one.
Reply with one section per project, each starting with a line \`### Project: <name> [<id>]\`, holding only the status note and then one line per record you changed or created with its id. Do not restate the review: the owner's digest already lists the facts.

## On request
- "Where are we on X": call review_projects for that project and answer with its milestones, what is overdue, what is due this week and what is blocked, naming owners.
- Rescheduling or reassigning tasks: update the task's due_date or assigned_user_id as asked and report each change with the record id.
- Milestone dates: never change a milestone's due date silently. Reply with a proposal (the milestone, its current date, the proposed date, the tasks due after it and whether they move too) and ask the owner to confirm. Update the milestone only when your task says the owner confirmed.
- Slack messages go only through a step an admin approved; otherwise say what you would send and to whom.`,
  model: 'sonnet',
  effort: 'medium',
  capabilities: ['read_records', 'write_records', 'picklists', 'review_projects', 'send_slack_dm', 'draft_email', 'run_on_schedule'],
  scope: {
    read: [PROJECTS_APP_TYPES.project, PROJECTS_APP_TYPES.milestone, PROJECTS_APP_TYPES.task, 'Person', 'Organization'],
    write: [PROJECTS_APP_TYPES.project, PROJECTS_APP_TYPES.milestone, PROJECTS_APP_TYPES.task],
  },
  schedule: {
    cron: '0 8 * * 1',
    brief:
      'Run the weekly project review: review every active project, list the tasks overdue and due this week under each milestone, flag milestones at risk, draft a client status note per project for its owner, and raise a RAID item for any blocker that recurs.',
  },
};

/**
 * Projects with milestones, a task board on the built-in Task type, a Projects nav category, and the Project Manager
 * persona. Milestones and tasks link to their project through a lookup field over the app's PART_OF edge, and a task
 * to its milestone over FOR_MILESTONE: Task is redefined for the tenant with every built-in field kept plus the
 * project and milestone lookups and a blocker note, the task_status picklist gains a Blocked item, and the board shows
 * each task's project, filterable by assignee and due date. Two more task tables carry fixed filters: open tasks due
 * within a week, and open tasks past their due date, both soonest first. Also the sample the install script defaults
 * to.
 */
export const PROJECTS_APP_MANIFEST: AppManifestInput = {
  key: 'projects',
  name: 'Projects',
  description: 'Projects, their milestones and a task board.',
  objectTypes: [
    {
      key: 'app:projects:Project',
      displayName: 'Project',
      displayNamePlural: 'Projects',
      icon: 'FolderKanban',
      color: '#0ea5e9',
      description: 'A piece of work with an owner, a budget and a target date',
      fields: [
        { key: 'name', label: 'Name', type: 'text', required: true },
        { key: 'status', label: 'Status', type: 'select', required: true, picklistName: 'project_status' },
        { key: 'owner_user_id', label: 'Owner', type: 'user' },
        { key: 'start_date', label: 'Start', type: 'date' },
        { key: 'target_date', label: 'Target Date', type: 'date' },
        { key: 'budget', label: 'Budget', type: 'number', format: 'currency' },
        { key: 'summary', label: 'Summary', type: 'textarea', newLine: true },
      ],
      naturalKeyField: 'name',
      relationshipSections: [
        {
          sectionLabel: 'Milestones',
          icon: 'Flag',
          edgeTypes: ['app:projects:PART_OF'],
          direction: 'incoming',
          relatedNodeTypes: ['app:projects:Milestone'],
          renderMode: 'grid',
          columns: [
            { field: 'name', headerName: 'Milestone', flex: 2, minWidth: 160, source: 'node', editable: false },
            { field: 'due_date', headerName: 'Due', width: 130, type: 'date', source: 'node', editable: false },
            { field: 'status', headerName: 'Status', width: 130, source: 'node', editable: false },
          ],
        },
        {
          sectionLabel: 'Tasks',
          icon: 'CheckSquare',
          edgeTypes: ['app:projects:PART_OF'],
          direction: 'incoming',
          relatedNodeTypes: ['Task'],
          renderMode: 'grid',
          columns: [
            { field: 'title', headerName: 'Task', flex: 2, minWidth: 160, source: 'node', editable: false },
            { field: 'status', headerName: 'Status', width: 130, source: 'node', editable: false },
            { field: 'due_date', headerName: 'Due', width: 130, type: 'date', source: 'node', editable: false },
          ],
        },
      ],
    },
    {
      key: 'app:projects:Milestone',
      displayName: 'Milestone',
      displayNamePlural: 'Milestones',
      icon: 'Flag',
      color: '#f59e0b',
      fields: [
        { key: 'name', label: 'Name', type: 'text', required: true },
        { key: 'status', label: 'Status', type: 'select', required: true, picklistName: 'milestone_status' },
        { key: 'due_date', label: 'Due Date', type: 'date' },
        {
          key: 'project',
          label: 'Project',
          type: 'lookup',
          lookupConfig: { nodeType: 'app:projects:Project', edgeType: 'app:projects:PART_OF', direction: 'outgoing' },
        },
      ],
      relationshipSections: [
        {
          sectionLabel: 'Project',
          icon: 'FolderKanban',
          edgeTypes: ['app:projects:PART_OF'],
          direction: 'outgoing',
          relatedNodeTypes: ['app:projects:Project'],
          renderMode: 'grid',
          columns: [
            { field: 'name', headerName: 'Project', flex: 2, minWidth: 160, source: 'node', editable: false },
            { field: 'status', headerName: 'Status', width: 130, source: 'node', editable: false },
          ],
        },
        {
          sectionLabel: 'Tasks',
          icon: 'CheckSquare',
          edgeTypes: ['app:projects:FOR_MILESTONE'],
          direction: 'incoming',
          relatedNodeTypes: ['Task'],
          renderMode: 'grid',
          columns: [
            { field: 'title', headerName: 'Task', flex: 2, minWidth: 160, source: 'node', editable: false },
            { field: 'status', headerName: 'Status', width: 130, source: 'node', editable: false },
            { field: 'due_date', headerName: 'Due', width: 130, type: 'date', source: 'node', editable: false },
          ],
        },
      ],
    },
    {
      // The built-in Task, redefined for this workspace: every built-in field kept, plus the project and milestone it
      // belongs to and, for a blocked task, what blocks it (the Project Manager reads it to spot a blocker that recurs)
      key: 'Task',
      displayName: 'Task',
      displayNamePlural: 'Tasks',
      icon: 'CheckSquare',
      color: '#10b981',
      fields: [
        { key: 'title', label: 'Title', type: 'text', required: true },
        { key: 'status', label: 'Status', type: 'select', required: true, picklistName: 'task_status' },
        { key: 'due_date', label: 'Due Date', type: 'date' },
        { key: 'assigned_user_id', label: 'Assigned To', type: 'user' },
        {
          key: 'project',
          label: 'Project',
          type: 'lookup',
          lookupConfig: { nodeType: 'app:projects:Project', edgeType: 'app:projects:PART_OF', direction: 'outgoing' },
        },
        {
          key: 'milestone',
          label: 'Milestone',
          type: 'lookup',
          lookupConfig: { nodeType: 'app:projects:Milestone', edgeType: 'app:projects:FOR_MILESTONE', direction: 'outgoing' },
        },
        { key: 'blocker', label: 'Blocked By', type: 'textarea', newLine: true },
      ],
      relationshipSections: [
        {
          sectionLabel: 'Project',
          icon: 'FolderKanban',
          edgeTypes: ['app:projects:PART_OF'],
          direction: 'outgoing',
          relatedNodeTypes: ['app:projects:Project'],
          renderMode: 'grid',
          columns: [
            { field: 'name', headerName: 'Project', flex: 2, minWidth: 160, source: 'node', editable: false },
            { field: 'status', headerName: 'Status', width: 130, source: 'node', editable: false },
          ],
        },
        {
          sectionLabel: 'Milestone',
          icon: 'Flag',
          edgeTypes: ['app:projects:FOR_MILESTONE'],
          direction: 'outgoing',
          relatedNodeTypes: ['app:projects:Milestone'],
          renderMode: 'grid',
          columns: [
            { field: 'name', headerName: 'Milestone', flex: 2, minWidth: 160, source: 'node', editable: false },
            { field: 'due_date', headerName: 'Due', width: 130, type: 'date', source: 'node', editable: false },
          ],
        },
        {
          sectionLabel: 'Assigned To',
          icon: 'UserCheck',
          edgeTypes: ['ASSIGNED_TO'],
          direction: 'outgoing',
          renderMode: 'grid',
          columns: [
            { field: 'name', headerName: 'Name', flex: 2, minWidth: 150, source: 'node', editable: false },
            { field: 'email', headerName: 'Email', flex: 1, minWidth: 150, source: 'node', editable: false },
          ],
        },
        {
          sectionLabel: 'Related To',
          icon: 'Link',
          edgeTypes: ['RELATED_TO'],
          direction: 'outgoing',
          renderMode: 'grid',
          columns: [
            { field: 'name', headerName: 'Name', flex: 2, minWidth: 150, source: 'node', editable: false },
            { field: 'type', headerName: 'Type', width: 120, source: 'node', editable: false },
          ],
        },
      ],
    },
  ],
  edgeTypes: [
    {
      key: 'app:projects:PART_OF',
      displayName: 'Part Of',
      reverseDisplayName: 'Contains',
      sourceTypes: ['app:projects:Milestone', 'Task'],
      targetTypes: ['app:projects:Project'],
    },
    {
      key: 'app:projects:FOR_MILESTONE',
      displayName: 'For Milestone',
      reverseDisplayName: 'Tasks',
      sourceTypes: ['Task'],
      targetTypes: ['app:projects:Milestone'],
    },
  ],
  views: [
    { key: 'projects-table', objectType: 'app:projects:Project', kind: 'table', columns: ['name', 'status', 'owner_user_id', 'target_date', 'budget'] },
    { key: 'milestones-table', objectType: 'app:projects:Milestone', kind: 'table', columns: ['name', 'status', 'due_date'] },
    {
      key: 'tasks-board',
      objectType: 'Task',
      kind: 'kanban',
      stageField: 'status',
      picklistName: 'task_status',
      cardRelation: { edgeType: 'app:projects:PART_OF', direction: 'outgoing', displayField: 'name', label: 'Project' },
      boardFilters: ['assigned_user_id', 'due_date'],
      defaultSort: { field: 'due_date', direction: 'asc' },
    },
    {
      // Open tasks due in the coming week, soonest first; overdue ones included since they are still due
      key: 'tasks-due-this-week',
      objectType: 'Task',
      kind: 'table',
      columns: ['title', 'status', 'due_date', 'assigned_user_id'],
      filters: [
        { field: 'status', op: 'in', value: ['pending', 'in_progress', 'blocked'] },
        { field: 'due_date', op: 'before', value: '+7d' },
      ],
      defaultSort: { field: 'due_date', direction: 'asc' },
    },
    {
      // Open tasks whose due date has passed, as of the reader's day
      key: 'tasks-overdue',
      objectType: 'Task',
      kind: 'table',
      columns: ['title', 'status', 'due_date', 'assigned_user_id'],
      filters: [
        { field: 'status', op: 'in', value: ['pending', 'in_progress', 'blocked'] },
        { field: 'due_date', op: 'before', value: 'today' },
      ],
      defaultSort: { field: 'due_date', direction: 'asc' },
    },
  ],
  nav: {
    category: { id: 'projects', label: 'Projects', order: 10 },
    items: [
      { path: '/projects', label: 'Projects', icon: 'FolderKanban', objectType: 'app:projects:Project', viewKey: 'projects-table' },
      { path: '/milestones', label: 'Milestones', icon: 'Flag', objectType: 'app:projects:Milestone', viewKey: 'milestones-table' },
      { path: '/task-board', label: 'Task Board', icon: 'Kanban', objectType: 'Task', viewKey: 'tasks-board' },
      { path: '/tasks-due-this-week', label: 'Due This Week', icon: 'CalendarClock', objectType: 'Task', viewKey: 'tasks-due-this-week' },
      { path: '/tasks-overdue', label: 'Overdue', icon: 'AlarmClock', objectType: 'Task', viewKey: 'tasks-overdue' },
    ],
  },
  picklists: [
    {
      // The built-in picklist keeps its items; installing appends Blocked, which the Project Manager treats as a blocker
      name: 'task_status',
      items: [
        { id: 'pending', label: 'Pending', order: 1 },
        { id: 'in_progress', label: 'In Progress', order: 2 },
        { id: 'blocked', label: 'Blocked', order: 3 },
        { id: 'completed', label: 'Completed', order: 4, is_closed: true },
        { id: 'cancelled', label: 'Cancelled', order: 5, is_closed: true },
      ],
    },
    {
      name: 'project_status',
      items: [
        { id: 'planned', label: 'Planned', order: 1, is_default: true },
        { id: 'active', label: 'Active', order: 2 },
        { id: 'on_hold', label: 'On Hold', order: 3 },
        { id: 'done', label: 'Done', order: 4, is_closed: true, is_won: true },
      ],
    },
    {
      name: 'milestone_status',
      items: [
        { id: 'open', label: 'Open', order: 1, is_default: true },
        { id: 'done', label: 'Done', order: 2, is_closed: true, is_won: true },
      ],
    },
  ],
  personas: [PROJECT_MANAGER_PERSONA],
};

export const PROJECTS_TEMPLATE: AppTemplate = {
  key: 'projects',
  name: 'Projects',
  description:
    'Project tracking: projects with status, owner, dates and budget; milestones linked to their project; a board of the workspace tasks by status, and tables of tasks due this week and overdue; and a Project Manager persona that runs the weekly review and drafts status notes.',
  tags: ['project', 'projects', 'project tracker', 'project management', 'project manager', 'milestone', 'milestones', 'deliverable', 'roadmap', 'initiative', 'workstream', 'budget', 'timeline', 'status note', 'weekly review', 'overdue'],
  revision: 5,
  manifest: PROJECTS_APP_MANIFEST,
};
